import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { CgProfile } from "react-icons/cg";
import { FiSearch } from "react-icons/fi";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Table from "react-bootstrap/Table";
import Message from "@features/shared/components/Message";
import Loader from "@features/shared/components/Loader";
import { getQueryDetails as getQueryDetailsAction } from "@redux/actions/queryDetails-actions";
import { useSearch } from "../hooks";
import "./QueryList.css";

/**
 * QueryItem component - displays a single query card
 */
const QueryItem = memo(({ data }) => {
  const displayName = data?.recType === "Dealer" ? data?.dealerName : data?.customerName;

  return (
    <Link to={`/query-details/${data?.Id}`} className="query-item-link">
      <div className="query__innerBox">
        <div className="query__header">
          <CgProfile className="query__profile" />
          <div className="query__detailName">
            <div className="query__profileName">{displayName}</div>
            <p className="query__profileSubName">{data?.CaseNumber}</p>
          </div>
        </div>
        <Table style={{ marginTop: "10px", marginBottom: "0px" }}>
          <tbody className="query__details" style={{ borderStyle: "hidden" }}>
            <tr className="query__subjectSection">
              <td className="col-4 col-md-6">Subject:</td>
              <td>{data?.Subject}</td>
            </tr>
            <tr>
              <td className="col-4 col-md-6">Description:</td>
              <td>{data?.Description}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    </Link>
  );
});

QueryItem.displayName = "QueryItem";

/**
 * QueryList component - displays list of service requests
 */
const QueryList = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Get queries from Redux state
  const queries = useSelector(
    (state) => state?.query?.queryDetails || [],
    (prev, next) => prev?.length === next?.length
  );

  // Custom search function
  const searchFunction = useCallback((record, searchTerm) => {
    const searchLower = searchTerm.toLowerCase();
    const nameToSearch = record?.recType === "Dealer" 
      ? record?.dealerName 
      : record?.customerName;
    return nameToSearch?.toLowerCase()?.includes(searchLower) || false;
  }, []);

  const { searchTerm, filteredData, hasNoResults, handleSearchChange } = useSearch(
    queries,
    searchFunction
  );

  // Fetch queries on mount using Redux action
  useEffect(() => {
    const fetchQueries = async () => {
      setLoading(true);
      await dispatch(getQueryDetailsAction());
      setLoading(false);
    };

    fetchQueries();
  }, [dispatch]);

  const hasQueries = useMemo(() => queries.length > 0, [queries]);

  return (
    <div className="query">
      <div className="row">
        <h6 className="query__name col-8">Query Details</h6>
      </div>

      <Loader loading={loading} />

      <div className="searchTour__searchBox">
        {hasQueries && (
          <div className="searchTour__searchTourBox">
            <FiSearch className="searchTour__searchIcon" />
            <input
              className="form-control"
              placeholder="Search query"
              aria-label="Search"
              type="search"
              name="search"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        )}

        <Link
          variant="contained"
          className={hasQueries ? "searchTour__newTourBtn" : "searchTour__newTourBtnNew"}
          to="/my-dealer"
          state={{ from: true }}
        >
          <BsFillPlusCircleFill className="searchTour__newTourIcon" />
          New SR
        </Link>
      </div>

      <div className="query__box">
        {!hasQueries && !loading && (
          <Message
            message="No pending queries"
            className="query__messageConatiner"
          />
        )}

        {hasNoResults && (
          <Message
            message={`No Queries related to "${searchTerm}"`}
            className="query__messageConatiner"
          />
        )}

        {!hasNoResults && filteredData.map((data) => (
          <QueryItem key={data?.Id} data={data} />
        ))}
      </div>
    </div>
  );
};

export default memo(QueryList);
